// src/ui/ConsumableTray.ts
//
// Phase 15 (UI-17, CONSUMABLE-03): tray of up to 3 consumable buttons.
// Renders below the board in RoundScene. Buttons are disabled mid-spin.

import Phaser from 'phaser';
import type { Rarity } from '../state/types';
import { addCrispText } from './textHelper';
import { consumableSpritesheet } from '../content/consumables';

export interface ConsumableSlot {
  id: string;
  defId: string;
  name: string;
  description: string;
  rarity: Rarity;
}

const RARITY_COLORS: Record<Rarity, number> = {
  common: 0xaaaaaa,
  uncommon: 0x5599ff,
  rare: 0xaa44ff,
  legendary: 0xffd700,
};

const SLOT_SIZE = 44;
const SLOT_SPACING = 54;
const DISABLED_ALPHA = 0.35;

export class ConsumableTray extends Phaser.GameObjects.Container {
  private slots: Array<{
    tile: Phaser.GameObjects.Rectangle;
    label: Phaser.GameObjects.Text;
    discardBtn: Phaser.GameObjects.Arc;
    id: string | null;
    rarity: Rarity;
  }> = [];

  onUse?: (id: string) => void;
  onDiscard?: (id: string) => void;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y);
    scene.add.existing(this);
  }

  draw(consumables: ReadonlyArray<ConsumableSlot>): void {
    this.removeAll(true);
    this.slots = [];

    const MAX_SLOTS = 3;
    const totalWidth = MAX_SLOTS * SLOT_SPACING;
    const startX = -totalWidth / 2 + SLOT_SPACING / 2;

    for (let i = 0; i < MAX_SLOTS; i++) {
      const cx = startX + i * SLOT_SPACING;
      const cons = consumables[i];

      if (cons) {
        // Square tile with rarity-colored border, dark fill
        const tile = this.scene.add
          .rectangle(cx, 0, SLOT_SIZE, SLOT_SIZE, 0x111111)
          .setStrokeStyle(2, RARITY_COLORS[cons.rarity])
          .setInteractive({ useHandCursor: true });
        this.add(tile);

        const sheet = consumableSpritesheet(cons.defId);
        const spriteKey = `consumable-${cons.defId}`;
        const hasSprite =
          (sheet && this.scene.textures.exists(sheet.key)) || this.scene.textures.exists(spriteKey);

        if (sheet && this.scene.textures.exists(sheet.key)) {
          const img = this.scene.add
            .image(cx, 0, sheet.key, sheet.frame)
            .setDisplaySize(SLOT_SIZE - 2, SLOT_SIZE - 2);
          this.add(img);
        } else if (this.scene.textures.exists(spriteKey)) {
          const img = this.scene.add
            .image(cx, 0, spriteKey)
            .setDisplaySize(SLOT_SIZE - 2, SLOT_SIZE - 2);
          this.add(img);
        }

        // Fallback letter label
        const label = addCrispText(this.scene, cx, -2, cons.name[0] ?? '?', {
          fontSize: '13px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
          .setOrigin(0.5, 0.5)
          .setVisible(!hasSprite);
        this.add(label);

        // Name tooltip below slot
        const tip = addCrispText(this.scene, cx, SLOT_SIZE / 2 + 5, cons.name, {
          fontSize: '9px',
          color: '#cccccc',
        }).setOrigin(0.5, 0);
        this.add(tip);

        tile.on('pointerdown', () => {
          if (this.onUse) this.onUse(cons.id);
        });

        // Discard ✕ button at top-right corner
        const discardBtn = this.scene.add
          .arc(cx + SLOT_SIZE / 2 - 5, -SLOT_SIZE / 2 + 5, 7, 0, 360, false, 0xcc2222)
          .setInteractive({ useHandCursor: true });
        const discardLabel = addCrispText(
          this.scene,
          cx + SLOT_SIZE / 2 - 5,
          -SLOT_SIZE / 2 + 4,
          '✕',
          { fontSize: '8px', color: '#ffffff' },
        ).setOrigin(0.5, 0.5);
        this.add(discardBtn);
        this.add(discardLabel);
        discardBtn.on('pointerdown', () => {
          if (this.onDiscard) this.onDiscard(cons.id);
        });

        this.slots.push({ tile, label, discardBtn, id: cons.id, rarity: cons.rarity });
      } else {
        // Empty slot
        const empty = this.scene.add
          .rectangle(cx, 0, SLOT_SIZE, SLOT_SIZE, 0x222222)
          .setStrokeStyle(1, 0x444444);
        this.add(empty);
      }
    }
  }

  setDisabled(disabled: boolean): void {
    for (const slot of this.slots) {
      if (disabled) {
        slot.tile.setFillStyle(0x333333).setStrokeStyle(2, 0x555555).setAlpha(DISABLED_ALPHA);
        slot.tile.disableInteractive();
        slot.discardBtn.setFillStyle(0x555555).setAlpha(DISABLED_ALPHA);
        slot.discardBtn.disableInteractive();
      } else {
        slot.tile.setFillStyle(0x111111).setStrokeStyle(2, RARITY_COLORS[slot.rarity]).setAlpha(1);
        slot.tile.setInteractive({ useHandCursor: true });
        slot.discardBtn.setFillStyle(0xcc2222).setAlpha(1);
        slot.discardBtn.setInteractive({ useHandCursor: true });
      }
    }
  }
}
